import React from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { Clock, GraduationCap, MapPin, Video } from "lucide-react";
import BadgeBoxCst from "@components/BadgeBoxCst";
import MoreActionsMenuCst from "@components/MoreActionsMenuCst";
import { ListItemAction } from "@components/ListItemCardCst/types";
import { EditionKind, EditionSession } from "../types";

interface SessionCardProps {
  session: EditionSession;
  kind: EditionKind;
  onVerifyInvites?: (session: EditionSession) => void;
  onDetails?: (session: EditionSession) => void;
  menuActions?: ListItemAction[] | ListItemAction[][];
}

const formatDateTime = (start?: number, end?: number): string => {
  if (!start) return "";
  const s = dayjs(start).locale("it");
  const e = end ? dayjs(end).locale("it") : null;
  const datePart = s.format("D MMMM YYYY");
  const timePart = e
    ? `${s.format("HH:mm")} - ${e.format("HH:mm")}`
    : s.format("HH:mm");
  return `${datePart} | ${timePart}`;
};

const joinLabels = (refs?: { label: string }[] | null): string =>
  (refs ?? []).map((r) => r.label).join(", ");

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  kind,
  onVerifyInvites,
  onDetails,
  menuActions,
}) => {
  const { t } = useTranslation(["edition-detail", "input"]);
  const theme = useTheme();

  const isVirtual = kind === "virtual";
  const faculties = joinLabels(session.faculties);
  const tutors = joinLabels(session.tutors);
  const toolLabel = session.tool
    ? t(`options.meeting_tool.${session.tool.toLowerCase()}`, {
        ns: "input",
      })
    : "";
  const virtualInfo = [toolLabel, session.link || session.url]
    .filter(Boolean)
    .join("  |  ");
  const physicalInfo = [session.site?.label, joinLabels(session.rooms)]
    .filter(Boolean)
    .join(" — ");

  const rowIcon = (icon: React.ReactNode, content: React.ReactNode) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        color: theme.palette.text.tertiary,
        fontSize: "13px",
      }}
    >
      {icon}
      {content}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        justifyContent: "space-between",
        gap: 2,
        padding: "16px 20px",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down("lg")]: {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          minWidth: 0,
        }}
      >
        {rowIcon(
          <Clock size={14} />,
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {formatDateTime(session.start_date, session.end_date)}
          </Typography>,
        )}

        {isVirtual
          ? !!virtualInfo &&
            rowIcon(
              <Video size={17} />,
              <Typography
                sx={{
                  fontSize: "13px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {virtualInfo}
              </Typography>,
            )
          : !!physicalInfo &&
            rowIcon(
              <MapPin size={17} />,
              <Typography sx={{ fontSize: "13px" }}>{physicalInfo}</Typography>,
            )}

        {(faculties || tutors) &&
          rowIcon(
            <GraduationCap size={14} />,
            <Typography sx={{ fontSize: "13px" }}>
              {[
                faculties && t("session.faculties", { names: faculties }),
                tutors && t("session.tutors", { names: tutors }),
              ]
                .filter(Boolean)
                .join("   ")}
            </Typography>,
          )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
          [theme.breakpoints.down("lg")]: {
            width: "100%",
            alignItems: "stretch",
          },
        }}
      >
        <Box
          sx={{
            [theme.breakpoints.down("lg")]: { alignSelf: "flex-start" },
          }}
        >
          {!session.confirmed && (
            <BadgeBoxCst variant="warning">
              {t("session.mail-to-send")}
            </BadgeBoxCst>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            [theme.breakpoints.down("lg")]: {
              justifyContent: "flex-start",
              flexWrap: "wrap",
            },
          }}
        >
          <Button
            variant="contained"
            onClick={() => onVerifyInvites?.(session)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
            }}
          >
            {t("session.verify-invites")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => onDetails?.(session)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            }}
          >
            {t("session.details")}
          </Button>
          {menuActions && (
            <MoreActionsMenuCst
              actions={menuActions}
              ariaLabel="session actions"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SessionCard;
